import React from 'react';
import { useTranslation } from 'react-i18next';
import { inputClass } from '../../pages/picture-ai/usePictureAi';

/**
 * The closed set of controls the admin form can render for any agent.
 *
 * An agent that sticks to these kinds needs no React of its own. Anything richer declares a named
 * widget instead, which the registry resolves — and falls back on when it does not know it.
 */
const GenericField = ({ field, value, onChange, disabled }) => {
  // Descriptor labels are English strings used as translation keys, so the same descriptor renders
  // in every language the platform supports.
  const { t } = useTranslation();

  const label = (
    <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
      {t(field.label)}
      {field.required && <span className="ml-1 text-red-500">*</span>}
    </span>
  );

  const help = field.help && (
    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{t(field.help)}</span>
  );

  if (field.kind === 'textarea') {
    return (
      <label className="block">
        {label}
        <textarea
          className={`${inputClass} font-mono text-xs leading-relaxed`}
          rows={field.rows || 6}
          maxLength={field.maxLength}
          value={value ?? ''}
          disabled={disabled}
          placeholder={field.starter}
          onChange={event => onChange(event.target.value)}
        />
        {help}
      </label>
    );
  }

  if (field.kind === 'select') {
    return (
      <label className="block">
        {label}
        <select className={inputClass} value={value ?? ''} disabled={disabled} onChange={event => onChange(event.target.value)}>
          {(field.options || []).map(option => (
            <option key={option.value} value={option.value}>{t(option.label)}</option>
          ))}
        </select>
        {help}
      </label>
    );
  }

  if (field.kind === 'switch') {
    return (
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={!!value} disabled={disabled} onChange={event => onChange(event.target.checked)} />
        <span className="text-sm text-gray-700 dark:text-gray-200">{t(field.label)}</span>
        {help}
      </label>
    );
  }

  if (field.kind === 'number') {
    return (
      <label className="block">
        {label}
        <input
          type="number" className={inputClass} value={value ?? ''} disabled={disabled}
          min={field.min} max={field.max}
          onChange={event => onChange(event.target.value === '' ? null : Number(event.target.value))}
        />
        {help}
      </label>
    );
  }

  if (field.kind === 'string-list') {
    return (
      <label className="block">
        {label}
        <textarea
          className={inputClass} rows={field.rows || 4}
          value={(value || []).join('\n')} disabled={disabled}
          onChange={event => onChange(event.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
        />
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{t(field.help || 'One value per line.')}</span>
      </label>
    );
  }

  return (
    <label className="block">
      {label}
      <input type="text" className={inputClass} value={value ?? ''} disabled={disabled} onChange={event => onChange(event.target.value)} />
      {help}
    </label>
  );
};

export default GenericField;
